/**
 * Catastrophic Plan Questionnaire Schema
 * 
 * This schema defines the questions needed to generate a complete Catastrophic Plan
 * for business succession planning.
 */

export const catastrophicQuestionnaireSchema = {
  sections: [
    {
      id: "businessInfo",
      title: "Business Information",
      description: "Basic information about your business",
      fields: [
        {
          id: "ownerName",
          type: "text",
          label: "Owner Name",
          placeholder: "Full legal name of current business owner",
          required: true
        },
        {
          id: "ownerAddress",
          type: "text",
          label: "Owner Office Address",
          placeholder: "Complete business address",
          required: true
        },
        {
          id: "businessName",
          type: "text",
          label: "Business Name",
          placeholder: "Legal name of your practice/business",
          required: true
        },
        {
          id: "businessType",
          type: "radio",
          label: "Business Type",
          options: [
            { value: "financial_advisor", label: "Financial Advisory Practice" },
            { value: "insurance_agent", label: "Insurance Agency" },
            { value: "financial_planner", label: "Financial Planning Practice" },
            { value: "wealth_manager", label: "Wealth Management Firm" },
            { value: "other", label: "Other Financial Services" }
          ],
          required: true
        },
        {
          id: "yearEstablished",
          type: "text",
          label: "Year Established",
          required: true
        },
        {
          id: "aum",
          type: "text",
          label: "Assets Under Management (if applicable)",
          placeholder: "Total AUM in dollars",
          required: false
        },
        {
          id: "annualRevenue",
          type: "text",
          label: "Approximate Annual Revenue",
          placeholder: "Annual revenue in dollars",
          required: true
        }
      ]
    },
    {
      id: "successorInfo",
      title: "Successor Information",
      description: "Information about your intended successor (if known)",
      fields: [
        {
          id: "hasSuccessor",
          type: "radio",
          label: "Do you already have a successor identified?",
          options: [
            { value: "true", label: "Yes" },
            { value: "false", label: "No" }
          ],
          required: true
        },
        {
          id: "successorName",
          type: "text",
          label: "Successor Name",
          placeholder: "Full legal name of intended successor",
          conditional: {
            field: "successorInfo.hasSuccessor",
            value: true
          }
        },
        {
          id: "successorAddress",
          type: "text",
          label: "Successor Office Address",
          placeholder: "Complete business address of successor",
          conditional: {
            field: "successorInfo.hasSuccessor",
            value: true
          }
        },
        {
          id: "successorMarketplace",
          type: "radio",
          label: "Would you like to find a succession partner on our marketplace?",
          conditional: {
            field: "successorInfo.hasSuccessor",
            value: false
          },
          options: [
            { value: "true", label: "Yes" },
            { value: "false", label: "No" }
          ],
        }
      ]
    },
    {
      id: "triggerEvents",
      title: "Trigger Events",
      description: "Events that will trigger the catastrophic plan",
      fields: [
        {
          id: "includeDeath",
          type: "checkbox",
          label: "Include death as a trigger event",
          options: [
            { value: "death", label: "Death" }
          ],
          required: true
        },
        {
          id: "includeDisability",
          type: "checkbox",
          label: "Include permanent disability as a trigger event",
          options: [
            { value: "disability", label: "Permanent Disability" }
          ],
          required: true
        },
        {
          id: "disabilityDefinition",
          type: "radio",
          label: "Definition of permanent disability",
          options: [
            { value: "unable_6_months", label: "Unable to perform duties for 6 consecutive months" },
            { value: "unable_12_months", label: "Unable to perform duties for 12 consecutive months" },
            { value: "physician_determination", label: "As determined by licensed physician" },
            { value: "custom", label: "Custom definition (to be specified by legal counsel)" }
          ],
          conditional: {
            field: "includeDisability",
            value: "disability"
          }
        },
        {
          id: "includeRetirement",
          type: "checkbox",
          label: "Include retirement as a trigger event",
          options: [
            { value: "retirement", label: "Retirement" }
          ],
          required: true
        },
        {
          id: "retirementDefinition",
          type: "radio",
          label: "Definition of retirement",
          options: [
            { value: "age_65", label: "Reaching age 65" },
            { value: "age_67", label: "Reaching age 67" },
            { value: "age_70", label: "Reaching age 70" },
            { value: "owner_discretion", label: "At owner's discretion (with notice period)" },
            { value: "custom", label: "Custom definition (to be specified by legal counsel)" }
          ],
          conditional: {
            field: "includeRetirement",
            value: "retirement"
          }
        },
        {
          id: "includeSale",
          type: "checkbox",
          label: "Include voluntary sale as a trigger event",
          options: [
            { value: "sale", label: "Voluntary Sale" }
          ],
          required: true
        },
        {
          id: "otherTriggerEvents",
          type: "text",
          label: "Other trigger events (specify)",
          placeholder: "Any other events that should trigger this agreement",
          required: false
        }
      ]
    },
    {
      id: "valuationMethodology",
      title: "Valuation Methodology",
      description: "How the business will be valued",
      fields: [
        {
          id: "valuationMethod",
          type: "radio",
          label: "Primary valuation method",
          options: [
            { value: "revenue_multiple", label: "Multiple of annual revenue" },
            { value: "earnings_multiple", label: "Multiple of earnings (EBITDA)" },
            { value: "aum_percentage", label: "Percentage of Assets Under Management" },
            { value: "book_value", label: "Book value plus premium" },
            { value: "independent_appraisal", label: "Independent professional appraisal" },
            { value: "agreed_value", label: "Pre-agreed fixed value (updated periodically)" },
            { value: "custom_formula", label: "Custom formula (to be specified)" }
          ],
          required: true
        },
        {
          id: "revenueMultiple",
          type: "text",
          label: "Revenue multiple",
          placeholder: "e.g., 2.5 means 2.5 times annual revenue",
          conditional: {
            field: "valuationMethod",
            value: "revenue_multiple"
          }
        },
        {
          id: "earningsMultiple",
          type: "text",
          label: "Earnings multiple",
          placeholder: "e.g., 6 means 6 times annual EBITDA",
          conditional: {
            field: "valuationMethod",
            value: "earnings_multiple"
          }
        },
        {
          id: "aumPercentage",
          type: "text",
          label: "AUM percentage",
          placeholder: "e.g., 2.0 means 2% of total AUM",
          conditional: {
            field: "valuationMethod",
            value: "aum_percentage"
          }
        },
        {
          id: "bookValuePremium",
          type: "text",
          label: "Book value premium percentage",
          placeholder: "e.g., 30 means book value plus 30%",
          conditional: {
            field: "valuationMethod",
            value: "book_value"
          }
        },
        {
          id: "appraisalMethod",
          type: "radio",
          label: "Appraisal method",
          options: [
            { value: "mutual_agreement", label: "Mutually agreed appraiser" },
            { value: "separate_appraisers", label: "Separate appraisers with referee if needed" },
            { value: "specified_firm", label: "Pre-specified appraisal firm" }
          ],
          conditional: {
            field: "valuationMethod",
            value: "independent_appraisal"
          }
        },
        {
          id: "agreedValue",
          type: "text",
          label: "Current agreed value",
          placeholder: "Value in dollars",
          conditional: {
            field: "valuationMethod",
            value: "agreed_value"
          }
        },
        {
          id: "valueUpdateFrequency",
          type: "radio",
          label: "How often should the agreed value be updated?",
          options: [
            { value: "annual", label: "Annually" },
            { value: "biannual", label: "Every 2 years" },
            { value: "triannual", label: "Every 3 years" }
          ],
          conditional: {
            field: "valuationMethod",
            value: "agreed_value"
          }
        },
        {
          id: "customFormula",
          type: "text",
          label: "Custom valuation formula",
          placeholder: "Describe your preferred formula",
          conditional: {
            field: "valuationMethod",
            value: "custom_formula"
          }
        },
        {
          id: "includeTangibleAssets",
          type: "checkbox",
          label: "Include tangible assets in valuation",
          options: [
            { value: "tangible_assets", label: "Include tangible assets" }
          ],
          required: true
        },
        {
          id: "includeAccounts",
          type: "checkbox",
          label: "Include client accounts/book of business",
          options: [
            { value: "accounts", label: "Include client accounts" }
          ],
          required: true
        },
        {
          id: "includeBrandValue",
          type: "checkbox",
          label: "Include brand value/goodwill",
          options: [
            { value: "brand_value", label: "Include brand value" }
          ],
          required: true
        },
        {
          id: "excludedAssets",
          type: "text",
          label: "Assets specifically excluded from valuation",
          placeholder: "List any assets to be excluded",
          required: false
        }
      ]
    },
    {
      id: "paymentTerms",
      title: "Payment Terms",
      description: "How the business will be paid for",
      fields: [
        {
          id: "paymentStructure",
          type: "radio",
          label: "Payment structure",
          options: [
            { value: "lump_sum", label: "100% lump sum payment at closing" },
            { value: "down_installments", label: "Down payment with installments" },
            { value: "all_installments", label: "100% installment payments" },
            { value: "earn_out", label: "Earn-out based on practice performance" }
          ],
          required: true
        },
        {
          id: "downPaymentPercentage",
          type: "text",
          label: "Down payment percentage",
          placeholder: "e.g., 25 means 25% down payment",
          conditional: {
            field: "paymentStructure",
            value: "down_installments"
          }
        },
        {
          id: "installmentPeriod",
          type: "text",
          label: "Installment period (years)",
          placeholder: "Number of years for installment payments",
          conditional: {
            field: "paymentStructure",
            values: ["down_installments", "all_installments"]
          }
        },
        {
          id: "installmentFrequency",
          type: "radio",
          label: "Installment frequency",
          options: [
            { value: "monthly", label: "Monthly" },
            { value: "quarterly", label: "Quarterly" },
            { value: "semi_annual", label: "Semi-annually" },
            { value: "annual", label: "Annually" }
          ],
          conditional: {
            field: "paymentStructure",
            values: ["down_installments", "all_installments"]
          }
        },
        {
          id: "interestRate",
          type: "text",
          label: "Interest rate on unpaid balance (%)",
          placeholder: "e.g., 5.5 means 5.5% interest rate",
          conditional: {
            field: "paymentStructure",
            values: ["down_installments", "all_installments"]
          }
        },
        {
          id: "earnOutPeriod",
          type: "text",
          label: "Earn-out period (years)",
          placeholder: "Number of years for earn-out calculation",
          conditional: {
            field: "paymentStructure",
            value: "earn_out"
          }
        },
        {
          id: "earnOutMetric",
          type: "radio",
          label: "Earn-out calculation based on",
          options: [
            { value: "revenue_retention", label: "Client revenue retention" },
            { value: "aum_retention", label: "AUM retention" },
            { value: "client_count_retention", label: "Client count retention" },
            { value: "new_revenue", label: "New revenue generation" },
            { value: "custom", label: "Custom earn-out formula" }
          ],
          conditional: {
            field: "paymentStructure",
            value: "earn_out"
          }
        },
        {
          id: "securitization",
          type: "radio",
          label: "How will the payment obligation be secured?",
          options: [
            { value: "none", label: "No specific security" },
            { value: "business_assets", label: "Business assets" },
            { value: "personal_guarantee", label: "Personal guarantee" },
            { value: "life_insurance", label: "Life insurance policy" },
            { value: "escrow", label: "Escrow account" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "securityDetails",
          type: "text",
          label: "Security details",
          placeholder: "Additional details about security arrangement",
          conditional: {
            field: "securitization",
            value: "other"
          }
        }
      ]
    },
    {
      id: "clientTransition",
      title: "Client Transition",
      description: "How clients will be transitioned to the successor",
      fields: [
        {
          id: "transitionPeriod",
          type: "text",
          label: "Client transition period (months)",
          placeholder: "Number of months for transition period",
          required: true
        },
        {
          id: "ownerInvolvement",
          type: "radio",
          label: "Owner's involvement during transition",
          options: [
            { value: "none", label: "No involvement required" },
            { value: "limited", label: "Limited consulting availability" },
            { value: "moderate", label: "Moderate involvement (part-time)" },
            { value: "significant", label: "Significant involvement (nearly full-time)" }
          ],
          required: true
        },
        {
          id: "ownerCompensation",
          type: "radio",
          label: "Will owner be compensated during transition?",
          options: [
            { value: "none", label: "No additional compensation" },
            { value: "hourly", label: "Hourly consulting rate" },
            { value: "flat_fee", label: "Flat fee for transition period" },
            { value: "percentage", label: "Percentage of revenue during transition" }
          ],
          required: true
        },
        {
          id: "compensationDetails",
          type: "text",
          label: "Compensation details",
          placeholder: "Specific terms of compensation",
          conditional: {
            field: "ownerCompensation",
            value: "none",
            operator: "not"
          }
        },
        {
          id: "clientNotification",
          type: "radio",
          label: "Client notification approach",
          options: [
            { value: "joint", label: "Joint announcement from owner and successor" },
            { value: "owner_first", label: "Owner announcement followed by successor introduction" },
            { value: "phased", label: "Phased approach based on client segments" },
            { value: "custom", label: "Custom approach" }
          ],
          required: true
        },
        {
          id: "clientApproval",
          type: "radio",
          label: "Will clients have right to approve transfer?",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" }
          ],
          required: true
        },
        {
          id: "retentionIncentives",
          type: "text",
          label: "Client retention incentives (if any)",
          placeholder: "Any special terms to encourage client retention",
          required: false
        }
      ]
    },
    {
      id: "legalRegulatory",
      title: "Legal and Regulatory",
      description: "Legal and regulatory considerations",
      fields: [
        {
          id: "governingLaw",
          type: "text",
          label: "Governing law (state/province)",
          placeholder: "e.g., California",
          required: true
        },
        {
          id: "regulatoryApprovals",
          type: "checkbox",
          label: "Required regulatory approvals",
          options: [
            { value: "none", label: "None required" },
            { value: "finra", label: "FINRA" },
            { value: "sec", label: "SEC" },
            { value: "state_insurance", label: "State Insurance Commissioner" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherApprovals",
          type: "text",
          label: "Other regulatory approvals",
          placeholder: "Specify other regulatory approvals",
          conditional: {
            field: "regulatoryApprovals",
            value: "other"
          }
        },
        {
          id: "nonCompete",
          type: "radio",
          label: "Include non-compete clause for owner",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" }
          ],
          required: true
        },
        {
          id: "nonCompeteDuration",
          type: "text",
          label: "Non-compete duration (years)",
          placeholder: "Duration in years",
          conditional: {
            field: "nonCompete",
            value: "yes"
          }
        },
        {
          id: "nonCompeteGeography",
          type: "text",
          label: "Non-compete geographic scope",
          placeholder: "e.g., Within 50 miles of current office",
          conditional: {
            field: "nonCompete",
            value: "yes"
          }
        },
        {
          id: "disputeResolution",
          type: "radio",
          label: "Dispute resolution method",
          options: [
            { value: "litigation", label: "Litigation in court" },
            { value: "mediation_first", label: "Mandatory mediation before litigation" },
            { value: "binding_arbitration", label: "Binding arbitration" },
            { value: "mediation_arbitration", label: "Mediation followed by binding arbitration" }
          ],
          required: true
        }
      ]
    },
    {
      id: "rightOfFirstRefusal",
      title: "Right of First Refusal",
      description: "Right of first refusal provisions",
      fields: [
        {
          id: "includeROFR",
          type: "radio",
          label: "Include Right of First Refusal clause",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" }
          ],
          required: true
        },
        {
          id: "rofrNegotiationPeriod",
          type: "text",
          label: "ROFR negotiation period (days)",
          placeholder: "e.g., 30 days",
          conditional: {
            field: "includeROFR",
            value: "yes"
          }
        },
        {
          id: "rofrMatchPeriod",
          type: "text",
          label: "ROFR matching period (days)",
          placeholder: "e.g., 10 days",
          conditional: {
            field: "includeROFR",
            value: "yes"
          }
        }
      ]
    },
    {
      id: "additionalProvisions",
      title: "Additional Provisions",
      description: "Additional provisions for the catastrophic plan",
      fields: [
        {
          id: "keyPersonInsurance",
          type: "radio",
          label: "Include key person insurance requirement",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" }
          ],
          required: true
        },
        {
          id: "insuranceAmount",
          type: "text",
          label: "Insurance policy amount",
          placeholder: "e.g., $1,000,000 or 'Equal to practice value'",
          conditional: {
            field: "keyPersonInsurance",
            value: "yes"
          }
        },
        {
          id: "clientAccessRestrictions",
          type: "text",
          label: "Client access restrictions (if any)",
          placeholder: "Any limitations on successor's access to clients prior to transfer",
          required: false
        },
        {
          id: "intellectualProperty",
          type: "text",
          label: "Intellectual property treatment",
          placeholder: "How IP will be handled in the transfer",
          required: false
        },
        {
          id: "otherProvisions",
          type: "text",
          label: "Other provisions or special requirements",
          placeholder: "Any other terms that should be included",
          required: false
        }
      ]
    }
  ]
}; 